import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../layouts/Navbar";
import Footer from "../../layouts/Footer";
import Sidebar from "../../layouts/Sidebar";
import DataTable from 'react-data-table-component';
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ViewQuestions = () => {
    const navigate = useNavigate();
    const [questions, setQuestions] = useState([]);
    const [filteredQuestions, setFilteredQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [selectedYear, setSelectedYear] = useState("all");
    const [categories, setCategories] = useState([]);
    const [years, setYears] = useState([]);
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    const [bulkAction, setBulkAction] = useState("");

    // Fetch questions on component mount
    useEffect(() => {
        fetchQuestions();
        fetchCategories();
    }, []);

    const fetchQuestions = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                "https://pastq.eddcodesgh.com/qApi/admin/questions/_viewquestions.php",
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                    },
                    withCredentials: true,
                }
            );

            if (response.data.status === "success") {
                const questionsData = response.data.records || [];
                setQuestions(questionsData);
                setFilteredQuestions(questionsData);
                
                // Extract unique years
                const uniqueYears = [...new Set(questionsData.map(q => q.year).filter(Boolean))].sort((a, b) => b - a);
                setYears(uniqueYears);
                
            } else {
                toast.error("Failed to load questions");
                setQuestions([]);
                setFilteredQuestions([]);
            }
        } catch (error) {
            console.error("Error fetching questions:", error.response || error);
            toast.error("Network error loading questions");
            setQuestions([]);
            setFilteredQuestions([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(
                "https://pastq.eddcodesgh.com/qApi/admin/categories/_viewcategories.php",
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                    },
                    withCredentials: true,
                }
            );

            // console.log("Categories API Response:", response.data); // Debug log

            if (response.data.status === "success") {
                const categoriesData = response.data.records || [];
                setCategories(categoriesData);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    // Apply filters whenever search, category, or year changes
    useEffect(() => {
        let result = questions;

        // Apply search filter
        if (search) {
            const searchLower = search.toLowerCase();
            result = result.filter(question =>
                question.title?.toLowerCase().includes(searchLower) ||
                question.description?.toLowerCase().includes(searchLower) ||
                question.category_name?.toLowerCase().includes(searchLower)
            );
        }

        if (selectedCategory !== "all") {
            // Use both category_id and cat_id for filtering
            result = result.filter(question => 
                question.category_id == selectedCategory || question.cat_id == selectedCategory
            );
        }

        // Apply year filter
        if (selectedYear !== "all") {
            result = result.filter(question => question.year == selectedYear);
        }

        setFilteredQuestions(result);
    }, [search, selectedCategory, selectedYear, questions]);

    const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this question? This action cannot be undone.")) {
        try {
            const response = await axios({
                method: 'DELETE',
                url: `https://pastq.eddcodesgh.com/qApi/admin/questions/delete.php?id=${id}`,
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506"
                },
                withCredentials: true
            });

            if (response.data.status === "success") {
                toast.success(response.data.message || "Question deleted successfully!");
                fetchQuestions();
            } else {
                toast.error(response.data.message || "Failed to delete question");
            }
        } catch (error) {
            console.error("Delete error:", error);
            
            // Better error handling
            if (error.response) {
                // Server responded with error status
                console.error("Response error:", error.response.data);
                console.error("Status:", error.response.status);
                
                if (error.response.status === 403) {
                    toast.error("Unauthorized: Invalid API key");
                } else if (error.response.status === 404) {
                    toast.error("Question not found");
                } else if (error.response.status === 400) {
                    toast.error("Invalid question ID");
                } else {
                    toast.error(error.response.data?.message || `Server error: ${error.response.status}`);
                }
            } else if (error.request) {
                // Request made but no response
                console.error("No response:", error.request);
                toast.error("No response from server. Check network connection.");
            } else {
                // Something else happened
                console.error("Request error:", error.message);
                toast.error(`Request error: ${error.message}`);
            }
        }
    }
};

    const handleBulkDelete = async () => {
        if (selectedQuestions.length === 0) {
            toast.warning("Please select questions to delete");
            return;
        }

        if (window.confirm(`Are you sure you want to delete ${selectedQuestions.length} selected question(s)?`)) {
            try {
                const response = await axios.post(
                    "https://pastq.eddcodesgh.com/qApi/admin/questions/bulk-delete.php",
                    { ids: selectedQuestions },
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                        },
                        withCredentials: true,
                    }
                );

                if (response.data.status === 200) {
                    toast.success(`Deleted ${selectedQuestions.length} question(s) successfully!`);
                    setSelectedQuestions([]);
                    fetchQuestions(); // Refresh the list
                } else {
                    toast.error(response.data.message || "Bulk delete failed");
                }
            } catch (error) {
                toast.error("Network error during bulk delete");
            }
        }
    };

    const handleBulkAction = () => {
        if (bulkAction === "delete") {
            handleBulkDelete();
        } else if (bulkAction === "export") {
            exportSelectedQuestions();
        }
    };

    const exportSelectedQuestions = () => {
        if (selectedQuestions.length === 0) {
            toast.warning("Please select questions to export");
            return;
        }

        // Create CSV content
        const selectedData = questions.filter(q => selectedQuestions.includes(q.id));
        const csvContent = [
            ["ID", "Title", "Category", "Year", "Type", "Questions", "Answers", "Upload Date"],
            ...selectedData.map(q => [
                q.id,
                q.title,
                q.category_name || 'Uncategorized',
                q.year,
                q.upload_type || 'pdf',
                q.question_file || 'No file',
                q.answer_file || 'No file',
                q.created_date || 'N/A'
            ])
        ].map(row => row.join(",")).join("\n");

        // Create download link
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `questions_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast.success(`Exported ${selectedQuestions.length} question(s) to CSV`);
    };

    const downloadFile = (filename, type) => {
        if (filename) {
            window.open(`https://pastq.eddcodesgh.com/qApi/uploads/questions/${filename}`, '_blank');
        } else {
            toast.warning(`No ${type} file available`);
        }
    };

    const columns = [
        {
            name: <div><input type="checkbox" onChange={(e) => {
                if (e.target.checked) {
                    setSelectedQuestions(filteredQuestions.map(q => q.id));
                } else {
                    setSelectedQuestions([]);
                }
            }} /></div>,
            selector: row => row.id,
            sortable: false,
            width: "50px",
            cell: row => (
                <input 
                    type="checkbox" 
                    checked={selectedQuestions.includes(row.id)}
                    onChange={(e) => {
                        if (e.target.checked) {
                            setSelectedQuestions([...selectedQuestions, row.id]);
                        } else {
                            setSelectedQuestions(selectedQuestions.filter(id => id !== row.id));
                        }
                    }}
                />
            )
        },
        // {
        //     name: "ID",
        //     selector: row => row.id,
        //     sortable: true,
        //     width: "80px"
        // },
        {
            name: "Title",
            selector: row => row.title,
            sortable: true,
            cell: row => (
                <div>
                    <strong className="d-block">{row.title}</strong>
                    {row.description && (
                        <small className="text-muted">
                            {row.description.length > 100 ? 
                                row.description.substring(0, 100) + '...' : 
                                row.description
                            }
                        </small>
                    )}
                </div>
            ),
            width: "250px"
        },
        {
            name: "Category",
            selector: row => row.category_name,
            sortable: true,
            cell: row => (
                <span className="badge bg-primary">
                    {row.category_name || 'Uncategorized'}
                </span>
            ),
            width: "150px"
        },
        {
            name: "Year",
            selector: row => row.year,
            sortable: true,
            width: "100px"
        },
        {
            name: "Type",
            selector: row => row.upload_type,
            sortable: true,
            cell: row => (
                <span className={`badge ${row.upload_type === 'pdf' ? 'bg-primary' : 'bg-success'}`}>
                    {row.upload_type ? row.upload_type.toUpperCase() : 'PDF'}
                </span>
            ),
            width: "100px"
        },
        {
            name: "Files",
            cell: row => (
                <div className="d-flex gap-2">
                    <button 
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => downloadFile(row.question_file, 'Question')}
                        disabled={!row.question_file}
                        title="Download Question PDF"
                    >
                        <i className="bi bi-file-pdf"></i> Q
                    </button>
                    <button 
                        className="btn btn-sm btn-outline-success"
                        onClick={() => downloadFile(row.answer_file, 'Answer')}
                        disabled={!row.answer_file}
                        title="Download Answer PDF"
                    >
                        <i className="bi bi-file-pdf"></i> A
                    </button>
                </div>
            ),
            width: "120px"
        },
        {
            name: "Upload Date",
            selector: row => row.created_date,
            sortable: true,
            width: "120px"
        },
        {
            name: "Actions",
            cell: row => (
                <div className="d-flex gap-2">
                    <Link 
                        to={`/admin/questions/${row.id}/edit`}
                        className="btn btn-sm btn-outline-primary"
                        title="Edit"
                    >
                        <i className="bi bi-pencil"></i>
                    </Link>
                    <button 
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(row.id)}
                        title="Delete"
                    >
                        <i className="bi bi-trash"></i>
                    </button>
                    {/* <Link 
                        to={`/admin/questions/${row.id}/view`}
                        className="btn btn-sm btn-outline-info"
                        title="View Details"
                    >
                        <i className="bi bi-eye"></i>
                    </Link> */}
                </div>
            ),
            width: "140px"
        }
    ];

    const customStyles = {
        headCells: {
            style: {
                fontWeight: 'bold',
                backgroundColor: '#f8f9fa',
                color: '#495057',
                fontSize: '14px',
                padding: '12px 8px'
            }
        },
        cells: {
            style: {
                fontSize: '14px',
                padding: '12px 8px',
                verticalAlign: 'middle'
            }
        },
        rows: {
            style: {
                minHeight: '60px',
                '&:hover': {
                    backgroundColor: '#f8f9fa'
                }
            }
        }
    };

    const resetFilters = () => {
        setSearch("");
        setSelectedCategory("all");
        setSelectedYear("all");
    };

    return (
        <>
            <Navbar />
            <Sidebar />
            
            <main id="main" className="main">
                <div className="pagetitle">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h1>Questions Management</h1>
                            <nav>
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/dashboard">Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item active">Questions</li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex gap-2">
                            <Link to="/admin/upload" className="btn btn-primary">
                                <i className="bi bi-cloud-upload me-2"></i>
                                Upload Questions
                            </Link>
                            <button 
                                className="btn btn-outline-secondary"
                                onClick={fetchQuestions}
                                title="Refresh"
                            >
                                <i className="bi bi-arrow-clockwise"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <section className="section">
                    <div className="row">
                        <div className="col-lg-12">
                            {/* Stats Cards */}
                            <div className="row mb-4">
                                <div className="col-md-3">
                                    <div className="card border-primary">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 className="card-title text-primary">Total Questions</h6>
                                                    <h2 className="mb-0">{questions.length}</h2>
                                                </div>
                                                <div className="bg-primary bg-opacity-10 rounded-3 p-3">
                                                    <i className="bi bi-file-text text-primary fs-3"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="card border-success">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 className="card-title text-success">PDF Questions</h6>
                                                    <h2 className="mb-0">
                                                        {questions.filter(q => q.upload_type === 'pdf' || !q.upload_type).length}
                                                    </h2>
                                                </div>
                                                <div className="bg-success bg-opacity-10 rounded-3 p-3">
                                                    <i className="bi bi-file-pdf text-success fs-3"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="card border-warning">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 className="card-title text-warning">Text Questions</h6>
                                                    <h2 className="mb-0">
                                                        {questions.filter(q => q.upload_type === 'text').length}
                                                    </h2>
                                                </div>
                                                <div className="bg-warning bg-opacity-10 rounded-3 p-3">
                                                    <i className="bi bi-textarea-t text-warning fs-3"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="card border-info">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 className="card-title text-info">Categories</h6>
                                                    <h2 className="mb-0">
                                                        {[...new Set(questions.map(q => q.category_name).filter(Boolean))].length}
                                                    </h2>
                                                </div>
                                                <div className="bg-info bg-opacity-10 rounded-3 p-3">
                                                    <i className="bi bi-folder text-info fs-3"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Main Card */}
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <div>
                                            <h5 className="card-title mb-0">Questions List</h5>
                                            <p className="text-muted mb-0">
                                                Manage all uploaded questions and their details
                                            </p>
                                        </div>
                                        <div className="text-muted">
                                            Showing {filteredQuestions.length} of {questions.length} questions
                                        </div>
                                    </div>

                                    {/* Bulk Actions & Filters */}
                                    <div className="row mb-4">
                                        <div className="col-md-8">
                                            <div className="d-flex align-items-center gap-3">
                                                {selectedQuestions.length > 0 && (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="badge bg-primary">
                                                            {selectedQuestions.length} selected
                                                        </span>
                                                        <select 
                                                            className="form-select form-select-sm" 
                                                            style={{ width: '150px' }}
                                                            value={bulkAction}
                                                            onChange={(e) => setBulkAction(e.target.value)}
                                                        >
                                                            <option value="">Bulk Action</option>
                                                            <option value="export">Export Selected</option>
                                                            <option value="delete">Delete Selected</option>
                                                        </select>
                                                        <button 
                                                            className="btn btn-sm btn-primary"
                                                            onClick={handleBulkAction}
                                                            disabled={!bulkAction}
                                                        >
                                                            Apply
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-4 text-end">
                                            <button 
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={resetFilters}
                                            >
                                                <i className="bi bi-x-circle me-2"></i>
                                                Reset Filters
                                            </button>
                                        </div>
                                    </div>

                                    {/* Filters Row */}
                                    <div className="row mb-4">
                                        <div className="col-md-4">
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <i className="bi bi-search"></i>
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search by title, description..."
                                                    value={search}
                                                    onChange={(e) => setSearch(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <select 
                                                className="form-select"
                                                value={selectedCategory}
                                                onChange={(e) => setSelectedCategory(e.target.value)}
                                            >
                                                <option value="all">All Categories</option>
                                                {categories.map(category => (
                                                    <option key={category.cat_id || category.id} value={category.cat_id || category.id}>
                                                        {category.cat_title || category.title} 
                                                        {category.years && ` (${category.years})`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-4">
                                            <select 
                                                className="form-select"
                                                value={selectedYear}
                                                onChange={(e) => setSelectedYear(e.target.value)}
                                            >
                                                <option value="all">All Years</option>
                                                {years.map(year => (
                                                    <option key={year} value={year}>
                                                        {year}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* DataTable */}
                                    {loading ? (
                                        <div className="text-center my-5 py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="mt-2 text-muted">Loading questions...</p>
                                        </div>
                                    ) : questions.length === 0 ? (
                                        <div className="text-center my-5 py-5">
                                            <i className="bi bi-file-text display-1 text-muted"></i>
                                            <h4 className="mt-3">No Questions Found</h4>
                                            <p className="text-muted mb-4">Start by uploading your first set of questions</p>
                                            <Link to="/admin/questions/upload" className="btn btn-primary btn-lg">
                                                <i className="bi bi-cloud-upload me-2"></i>
                                                Upload Questions
                                            </Link>
                                        </div>
                                    ) : (
                                        <DataTable
                                            columns={columns}
                                            data={filteredQuestions}
                                            customStyles={customStyles}
                                            fixedHeader
                                            fixedHeaderScrollHeight="500px"
                                            highlightOnHover
                                            pagination
                                            paginationPerPage={10}
                                            paginationRowsPerPageOptions={[5, 10, 15, 20]}
                                            noDataComponent={
                                                <div className="text-center py-5">
                                                    <i className="bi bi-search display-3 text-muted"></i>
                                                    <h4 className="mt-3">No matching questions found</h4>
                                                    <p className="text-muted">Try adjusting your search or filters</p>
                                                </div>
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />

            <style jsx>{`
                .card {
                    border-radius: 12px;
                    border: 1px solid rgba(0, 0, 0, 0.08);
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
                }
                
                .badge {
                    padding: 0.5em 0.8em;
                    font-weight: 500;
                }
                
                .btn-sm {
                    padding: 0.25rem 0.5rem;
                }
                
                .input-group-text {
                    background-color: #f8f9fa;
                }
                
                @media (max-width: 768px) {
                    .card-body {
                        padding: 1rem;
                    }
                    
                    .d-flex {
                        flex-direction: column;
                        gap: 10px;
                    }
                    
                    .btn {
                        width: 100%;
                    }
                }
            `}</style>
        </>
    );
};

export default ViewQuestions;